import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { ApiTokensPaginatedData } from '../../model/api-tokens/interfaces'
import type { ApiCallbacks, ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { getFirstQueryValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useApiTokensStore } from '../../model/api-tokens/store'

interface ApiTokensApiOptions extends ApiCallbacks<ApiTokensPaginatedData> {
  route?: RouteLocationNormalizedGeneric
}

export const useApiTokensApi = async (
  options?: ApiTokensApiOptions,
): Promise<ApiTokensPaginatedData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useApiTokensStore()

  const url = useAdminRoutes('admin.api-tokens', pick({
    user_id: store.getFilterUserId,
    search: store.getFilterSearch,
    per_page: getFirstQueryValue(route.query?.per_page) || store.getPerPage,
    page: getFirstQueryValue(route.query?.page) || store.getCurrentPage,
  }))

  options?.onStart?.()
  store.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options?.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options?.onError?.(data.message)
      return null
    }

    const paginatedData: ApiTokensPaginatedData | null = data.data || null
    store.setTokensPaginatedData(paginatedData)
    options?.onSuccess?.(paginatedData)
    return paginatedData
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options?.onError?.(msg)
    return null
  } finally {
    store.setLoading(false)
    options?.onFinish?.()
  }
}
